'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Edit2,
  Calendar as CalendarIcon,
  Phone,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Check
} from 'lucide-react';

// Ícono oficial exacto de WhatsApp (Burbuja de diálogo con auricular interior fiel a Cerini)
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M3 21l1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z" />
    <path d="M8.5 9.5c.3-.3.8-.3 1.1 0l1.2 1.2c.3.3.3.8 0 1.1-.3.3-.3.8 0 1.1.8.8 1.9 1.9 2.7 2.7.3.3.8.3 1.1 0 .3-.3.8-.3 1.1 0l1.2 1.2c.3.3.3.8 0 1.1-.6.6-1.5.8-2.3.4-3.1-1.6-5.5-4-7.1-7.1-.4-.8-.2-1.7.4-2.3z" />
  </svg>
);

interface AppointmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  businessName?: string;
  businessSlug?: string;
  branchName?: string;
  branchAddress?: string;
  branchPhone?: string;
  googleMapsUrl?: string;
  onReschedule?: (booking: any) => void;
  onCancelled?: (bookingId: string) => void;
}

export const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  isOpen,
  onClose,
  booking,
  businessName = 'Noir Atelier',
  businessSlug,
  branchName = 'Recoleta',
  branchAddress = 'Quintana 420, Recoleta, CABA',
  branchPhone = '011 4800-1815',
  googleMapsUrl,
  onReschedule,
  onCancelled
}) => {
  const [showCalendarPopover, setShowCalendarPopover] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsAppFeedback, setWhatsAppFeedback] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowCalendarPopover(false);
      }
    }
    if (showCalendarPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarPopover]);

  if (!isOpen || !booking) return null;

  // Formateo de fecha y hora
  const dateStr = booking.dateStr || booking.date || '';
  const startTime = booking.time || booking.startTime || '10:00';
  const duration = booking.duration || 30;

  // Parsear fecha
  let dateFormatted = dateStr;
  let isoDateStart = '';
  let isoDateEnd = '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const d = new Date(year, month, day, 12, 0, 0);
      const dayName = d.toLocaleDateString('es-AR', { weekday: 'long' });
      const monthName = d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '');
      dateFormatted = `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}, ${day} ${monthName} ${year}`;

      // ISO dates para Google Calendar
      const [sh, sm] = startTime.split(':').map(Number);
      const startD = new Date(year, month, day, sh || 10, sm || 0, 0);
      const endD = new Date(startD.getTime() + duration * 60000);
      isoDateStart = startD.toISOString().replace(/-|:|\.\d\d\d/g, '');
      isoDateEnd = endD.toISOString().replace(/-|:|\.\d\d\d/g, '');
    }
  } catch (e) {
    console.error('Error parseando fecha:', e);
  }

  // Formatear horas con AM / PM (ej: "11:00 AM - 11:30 AM")
  const formatTimeWithPeriod = (timeStr: string) => {
    if (!timeStr) return '10:00 AM';
    const [h, m] = timeStr.split(':').map(Number);
    const period = (h >= 12 && h < 24) ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m || 0).padStart(2, '0')} ${period}`;
  };

  const [sh, sm] = startTime.split(':').map(Number);
  const endMinutesTotal = (sh || 10) * 60 + (sm || 0) + duration;
  const endHour = Math.floor(endMinutesTotal / 60);
  const endMin = endMinutesTotal % 60;
  const endTimeCalculated = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
  const startTimeDisplay = formatTimeWithPeriod(startTime);
  const endTimeDisplay = formatTimeWithPeriod(booking.endTime || endTimeCalculated);

  // Precios
  const priceVal = booking.price || booking.totalPrice || 49000;
  const priceFormatted = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(priceVal);

  const serviceName = booking.serviceName || booking.service?.name || 'Corte de hombre';
  const staffName = booking.staffName || booking.staff?.name || 'Sebastián';
  const isCancelled = booking.status === 'CANCELLED' || booking.status === 'cancelled';

  // URLs de Calendarios
  const calTitle = encodeURIComponent(`${serviceName} con ${staffName} - ${businessName}`);
  const calLocation = encodeURIComponent(`${branchName} (${branchAddress})`);
  const calDetails = encodeURIComponent(`Tu turno en ${businessName} (${branchName}).\nServicio: ${serviceName}\nProfesional: ${staffName}`);

  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${calTitle}&dates=${isoDateStart}/${isoDateEnd}&details=${calDetails}&location=${calLocation}`;
  const outlookCalUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${calTitle}&body=${calDetails}&location=${calLocation}&startdt=${isoDateStart}&enddt=${isoDateEnd}`;

  // Enlace / Descarga iCal (.ics)
  const handleDownloadIcs = () => {
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Calenddy//Booking System//ES',
      'BEGIN:VEVENT',
      `SUMMARY:${serviceName} con ${staffName} - ${businessName}`,
      `DESCRIPTION:${serviceName} en ${branchName}`,
      `LOCATION:${branchAddress}`,
      `DTSTART:${isoDateStart}`,
      `DTEND:${isoDateEnd}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `turno-${serviceName.toLowerCase().replace(/\s+/g, '-')}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Despachar detalles por WhatsApp automáticamente por Evolution API (1-click estilo Cerini)
  const handleSendWhatsApp = async () => {
    if (sendingWhatsApp) return;
    setSendingWhatsApp(true);
    setWhatsAppFeedback(null);

    try {
      const res = await fetch(`/api/bookings/${booking.id}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setWhatsAppFeedback(`¡Enviado a tu WhatsApp (${data.recipientPhone || 'registrado'})!`);
      } else {
        setWhatsAppFeedback(data.error || 'No se pudo enviar el WhatsApp');
      }
    } catch {
      setWhatsAppFeedback('Error al comunicarse con WhatsApp');
    } finally {
      setSendingWhatsApp(false);
      setTimeout(() => setWhatsAppFeedback(null), 5000);
    }
  };

  // Ejecutar cancelación
  const handleConfirmCancellation = async () => {
    setCancelling(true);
    setCancelError('');

    try {
      const token = booking.accessToken || booking.securityCode || '';
      const res = await fetch(`/api/bookings/${booking.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'CANCELLED',
          accessToken: token,
          securityCode: token
        })
      });

      const data = await res.json().catch(() => ({ success: false }));
      const isSample = String(booking.id).startsWith('booking_sample_') || String(booking.id).startsWith('sample_');
      if (data.success || isSample) {
        // Actualizar en localStorage del negocio correspondiente
        try {
          const storageKey = businessSlug ? `calenddy_bookings_${businessSlug}` : 'calenddy_my_bookings';
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const list = JSON.parse(stored);
            const updated = list.map((item: any) =>
              item.id === booking.id ? { ...item, status: 'CANCELLED' } : item
            );
            localStorage.setItem(storageKey, JSON.stringify(updated));
          }
        } catch (e) {
          console.error(e);
        }

        setShowCancelConfirm(false);
        setShowCancelSuccess(true);
        if (onCancelled) onCancelled(booking.id);
      } else {
        setCancelError(data.error || 'No se pudo cancelar la cita.');
      }
    } catch (err: any) {
      setCancelError(err.message || 'Error de comunicación al cancelar.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-in fade-in duration-200">
      {/* Modal Principal: Detalles de la Cita (Réplica exacta Images 2 y 4) */}
      {!showCancelConfirm && !showCancelSuccess && (
        <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl relative animate-in zoom-in-95 duration-200 border border-gray-100">
          {/* Header del Modal */}
          <div className="flex items-center justify-between pb-3 mb-4">
            <h3 className="font-bold text-base sm:text-lg text-gray-900">
              {dateFormatted}
            </h3>

            {/* Iconos de Acción Superior */}
            <div className="flex items-center space-x-1 text-gray-400 relative">
              {!isCancelled ? (
                <>
                  {/* 1. Lápiz: Reagendar / Editar */}
                  {onReschedule && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onReschedule(booking);
                      }}
                      title="Reagendar / Editar cita"
                      className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition cursor-pointer"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* 2. WhatsApp: Ícono oficial exacto */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleSendWhatsApp}
                      disabled={sendingWhatsApp}
                      title="Enviar a tu WhatsApp"
                      className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-emerald-50 text-gray-400 hover:text-[#25D366] transition cursor-pointer disabled:opacity-50"
                    >
                      {sendingWhatsApp ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#25D366]" />
                      ) : (
                        <WhatsAppIcon className="w-4 h-4" />
                      )}
                    </button>
                    {whatsAppFeedback && (
                      <div className="absolute right-0 top-9 z-50 bg-gray-900 text-white text-[10px] py-1 px-2.5 rounded-md whitespace-nowrap shadow-lg animate-in fade-in">
                        {whatsAppFeedback}
                      </div>
                    )}
                  </div>

                  {/* 3. Calendario: Popover flotante Google / Outlook / iOS */}
                  <div className="relative" ref={popoverRef}>
                    <button
                      type="button"
                      onClick={() => setShowCalendarPopover(!showCalendarPopover)}
                      title="Agregar a calendario"
                      className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition cursor-pointer"
                    >
                      <CalendarIcon className="w-4 h-4" />
                    </button>

                    {/* Popover Dropdown de Calendarios (Fiel a Cerini) */}
                    {showCalendarPopover && (
                      <div className="absolute right-0 top-10 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 p-2.5 z-50 flex items-center justify-around animate-in zoom-in-95 duration-150">
                        {/* Google */}
                        <a
                          href={googleCalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center group p-1.5 rounded-xl hover:bg-gray-50 transition cursor-pointer"
                        >
                          <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center group-hover:scale-105 transition-transform">
                            <span className="text-xs font-bold text-blue-600">31</span>
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 mt-1">Google</span>
                        </a>

                        {/* Outlook */}
                        <a
                          href={outlookCalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center group p-1.5 rounded-xl hover:bg-gray-50 transition cursor-pointer"
                        >
                          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center group-hover:scale-105 transition-transform">
                            <span className="text-xs font-bold text-blue-700">O</span>
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 mt-1">Outlook</span>
                        </a>

                        {/* iOS iCal */}
                        <button
                          type="button"
                          onClick={() => {
                            handleDownloadIcs();
                            setShowCalendarPopover(false);
                          }}
                          className="flex flex-col items-center group p-1.5 rounded-xl hover:bg-gray-50 transition cursor-pointer"
                        >
                          <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center group-hover:scale-105 transition-transform">
                            <span className="text-xs font-bold text-red-600">iOS</span>
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 mt-1">iOS</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : null}

              {/* 4. Cerrar Modal (X) */}
              <button
                type="button"
                onClick={onClose}
                title="Cerrar"
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-900 transition cursor-pointer ml-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Fila Principal: Servicio y Horario a la Izquierda, Precio a la Derecha (Exacto a Imagen 2 y 4) */}
          <div className="flex items-start justify-between gap-4 mt-2 mb-6">
            <div className="flex-1">
              <h2 className="text-base sm:text-[17px] font-medium text-gray-900 leading-snug">
                {serviceName} con {staffName}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {startTimeDisplay} - {endTimeDisplay}
              </p>
              {isCancelled && (
                <div className="mt-2.5">
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium text-rose-500 bg-rose-50 border border-rose-100/60">
                    Cancelado por ti
                  </span>
                </div>
              )}
            </div>

            <div className="text-right shrink-0">
              <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider block">
                PRECIO
              </span>
              <span className="text-base sm:text-[18px] font-bold text-gray-900 block mt-0.5">
                {priceFormatted}
              </span>
            </div>
          </div>

          {/* Sección Ubicación: Sobria, pura sobre el fondo blanco, sin sombras ni cajas (Exacto a Cerini) */}
          <div className="mb-6">
            <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider block mb-1">
              UBICACIÓN
            </span>
            <p className="text-sm sm:text-[15px] font-medium text-gray-900">{businessName} - {branchName}</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{branchAddress || `${businessName} ${branchName}`}</p>
          </div>

          {/* Botones Inferiores de Acción: Solamente Cancelar y Confirmar si está activa; si está cancelada, NADA */}
          {!isCancelled && (
            <div className="flex items-center space-x-3 pt-3">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="flex-1 py-3 px-4 rounded-xl border border-red-500 text-red-500 hover:bg-red-50/50 font-medium text-sm transition text-center cursor-pointer shadow-2xs"
              >
                Cancelar cita
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl bg-[#111827] hover:bg-black text-white font-medium text-sm transition text-center cursor-pointer shadow-xs"
              >
                Confirmar cita
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal Confirmar Cancelación: "¿Estás seguro de cancelar la cita?" */}
      {showCancelConfirm && (
        <div className="bg-white rounded-3xl max-w-sm w-full p-6 sm:p-7 shadow-2xl relative animate-in zoom-in-95 duration-200 text-center border border-gray-100">
          {/* Círculo con Exclamación Verde */}
          <div className="w-16 h-16 rounded-full border-2 border-emerald-500 flex items-center justify-center mx-auto mb-4 text-emerald-500">
            <span className="text-3xl font-light">!</span>
          </div>

          <h3 className="text-lg font-bold text-gray-900 mb-6">
            ¿Estás seguro de cancelar la cita?
          </h3>

          {cancelError && (
            <p className="text-xs text-red-600 mb-3 bg-red-50 p-2 rounded-lg">
              {cancelError}
            </p>
          )}

          <div className="flex items-center justify-center space-x-3">
            <button
              type="button"
              disabled={cancelling}
              onClick={() => setShowCancelConfirm(false)}
              className="py-2.5 px-6 rounded-xl bg-slate-600 hover:bg-slate-700 text-white font-medium text-sm transition cursor-pointer shadow-xs"
            >
              No
            </button>
            <button
              type="button"
              disabled={cancelling}
              onClick={handleConfirmCancellation}
              className="py-2.5 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm transition cursor-pointer shadow-xs disabled:opacity-50"
            >
              {cancelling ? 'Cancelando...' : 'Si'}
            </button>
          </div>
        </div>
      )}

      {/* Modal Éxito: "Cita cancelada correctamente" (Réplica exacta Image 3) */}
      {showCancelSuccess && (
        <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl relative animate-in zoom-in-95 duration-200 text-center border border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 cursor-pointer p-1"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Círculo con Tilde Verde idéntico a Image 3 */}
          <div className="w-18 h-18 rounded-full border-2 border-[#10b981] flex items-center justify-center mx-auto mb-5 text-[#10b981]">
            <Check className="w-9 h-9 stroke-[2.5]" />
          </div>

          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 tracking-tight">
            Cita cancelada correctamente
          </h3>
          <p className="text-sm text-gray-500 mb-8 max-w-sm mx-auto leading-relaxed">
            Lamentamos que no puedas asistir a tu cita. Tu cita ha sido cancelada correctamente.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 px-4 rounded-xl bg-[#111827] hover:bg-black text-white font-medium text-sm sm:text-base transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <span>Entendido</span>
            <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
};
